import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Table,
} from "reactstrap";

import { adminPaths } from "admin/paths";
import { confirmAdminAction, sortByOption, toDateLabel } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { roleLabel } from "labels/tr";
import { userRoles } from "data/admin/domain";
import { usersRepo } from "data/repositories/usersRepo";

const ROLE_COLORS = {
  admin: "danger",
  business: "info",
  agency: "primary",
  guide: "warning",
  customer: "secondary",
};

const sortOptions = [
  { value: "updated-desc", label: "Son güncellenen" },
  { value: "name-asc", label: "Ada göre A-Z" },
  { value: "role-asc", label: "Rol göre" },
];

export default function AdminUsers() {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("active");
  const [sortBy, setSortBy] = useState("updated-desc");

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      setUsers(usersRepo.list({ includeDeleted: true }));
    } catch (loadError) {
      setError(loadError?.message || "Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = users.filter((user) => {
      if (stateFilter === "active" && user.deletedAt) return false;
      if (stateFilter === "archived" && !user.deletedAt) return false;
      if (roleFilter && user.role !== roleFilter) return false;
      if (!query) return true;
      return [user.uid, user.displayName, user.email, user.role].join(" ").toLowerCase().includes(query);
    });

    return sortByOption(next, sortBy, {
      "role-asc": (left, right) => String(left.role || "").localeCompare(String(right.role || ""), "tr"),
    });
  }, [roleFilter, search, sortBy, stateFilter, users]);

  const handleArchive = (user) => {
    if (!confirmAdminAction(user.displayName || user.uid)) return;
    try {
      usersRepo.remove(user.uid || user.id);
      setFlash({ color: "success", message: "Kullanıcı arşive alındı." });
      load();
    } catch (removeError) {
      setFlash({ color: "danger", message: removeError?.message || "Kullanıcı arşivlenemedi." });
    }
  };

  const handleRestore = (user) => {
    try {
      usersRepo.restore(user.uid || user.id);
      setFlash({ color: "success", message: "Kullanıcı tekrar yayına alındı." });
      load();
    } catch (restoreError) {
      setFlash({ color: "danger", message: restoreError?.message || "Kullanıcı geri alınamadı." });
    }
  };

  const resetFilters = () => {
    setSearch("");
    setRoleFilter("");
    setStateFilter("active");
    setSortBy("updated-desc");
  };

  return (
    <>
      <AdminPageHeader
        title="Yönetim — Kullanıcılar"
        description="Rol kümesi kimlik doğrulama katmanıyla uyumludur: müşteri, işletme, acente, rehber ve yönetici."
      />

      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader>
            <h3 className="mb-3">Kullanıcı kayıtları</h3>
            <AdminListControls
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Kimlik, ad, e-posta veya rol ara"
              filters={[
                {
                  key: "role",
                  label: "Rol",
                  value: roleFilter,
                  onChange: setRoleFilter,
                  options: [{ value: "", label: "Tüm roller" }, ...userRoles.map((role) => ({ value: role, label: roleLabel(role) }))],
                },
                {
                  key: "state",
                  label: "Kayıt durumu",
                  value: stateFilter,
                  onChange: setStateFilter,
                  options: [
                    { value: "active", label: "Aktif" },
                    { value: "archived", label: "Arşiv" },
                    { value: "all", label: "Tümü" },
                  ],
                },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={sortOptions}
              onReset={resetFilters}
              resultCount={filtered.length}
              totalCount={users.length}
            />
          </CardHeader>
          <CardBody>
            <AdminStateBlock
              flash={flash}
              onDismissFlash={() => setFlash(null)}
              error={error}
              onDismissError={() => setError("")}
              loading={loading}
              spinnerColor="dark"
              isEmpty={filtered.length === 0}
              emptyTitle="Henüz kullanıcı yok"
            >
              <Table className="align-items-center" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>Kimlik</th>
                    <th>Ad</th>
                    <th>E-posta</th>
                    <th>Rol</th>
                    <th>Son giriş</th>
                    <th>Durum</th>
                    <th className="text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => (
                    <tr key={user.uid}>
                      <td>
                        <small>{user.uid}</small>
                      </td>
                      <td>{user.displayName}</td>
                      <td>{user.email || "-"}</td>
                      <td>
                        <Badge color={ROLE_COLORS[user.role] || "secondary"} pill>{roleLabel(user.role)}</Badge>
                      </td>
                      <td>{toDateLabel(user.lastLoginAt)}</td>
                      <td>
                        <Badge color={user.deletedAt ? "warning" : "success"} pill>
                          {user.deletedAt ? "Arşivde" : "Aktif"}
                        </Badge>
                      </td>
                      <td className="text-right">
                        <Button color="primary" size="sm" className="mr-2" tag={Link} to={`/admin${adminPaths.admin.userDetail(user.uid)}`}>
                          Detay
                        </Button>
                        {user.deletedAt ? (
                          <Button color="success" outline size="sm" onClick={() => handleRestore(user)}>
                            Geri al
                          </Button>
                        ) : (
                          <Button color="danger" size="sm" onClick={() => handleArchive(user)}>
                            Arşivle
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </AdminStateBlock>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
